import { SearchOffRounded, SearchOutlined } from '@mui/icons-material'
import { Box, Card, CardContent, Chip, Divider, InputAdornment, List, ListItemButton, ListItemIcon, ListItemText, ListSubheader, TextField, Typography, useTheme } from '@mui/material'
import { ChangeEvent, FormEvent, useState } from 'react'
import _ from 'lodash'
import { useAppDispatch, useAppSelector } from '../../../store/Hooks'
import EventsEngine from '../../../engines/EventsEngine'
import RouteIcon from '../../routing/RouteIcon'
import { changeRoute } from '../../../store/slices/SessionSlice'
import { addSpotlightSearch } from '../../../store/slices/AppSlice'
import SpotlightManager, { AI_SPOTLIGHT, ISpotlightItem } from '../../../managers/SpotlightManager'
import useQuery, { buildQueryKey } from '../../hooks/useQuery'
import ApiClient from '../../../backend/ApiClient'
import { shallowEqual } from 'react-redux'
import PermissionManager from '../../../managers/PermissionManager'
import { RoutePermissions } from '../../../managers/PermissionManager'

interface P {
    defaultClosed?: boolean
}

function Spotlight({ defaultClosed }: P) {
    const currentUser = useAppSelector(state => state.app.currentUser)
    const dispatch = useAppDispatch()
    const theme = useTheme()
    const [text, setText] = useState('')
    const [searchResult, setSearchResult] = useState<ISpotlightItem[]>([])
    const [selected, setSelected] = useState<number>(-1)
    const history = useAppSelector(s => (s.app.spotlight?.history ?? []).slice().reverse().slice(0, 7), shallowEqual)
    const options = searchResult.length > 0 ? searchResult : SpotlightManager.historyToActions(history)

    useQuery({
        queryKey: buildQueryKey('contracts'),
        queryFn: async () => await ApiClient.getAllContracts(),
        cache: true
    })

    useQuery({
        queryKey: buildQueryKey('filter-Line Of Business'),
        queryFn: async () => await ApiClient.Filters.getLineOfBusiness(),
        cache: true
    })

    useQuery({
        queryKey: buildQueryKey('filter-Contract Type'),
        queryFn: async () => await ApiClient.Filters.getContractType(),
        cache: true
    })

    useQuery({
        queryKey: buildQueryKey('filter-Provider Groups'),
        queryFn: async () => await ApiClient.getAllCounterparties(),
        cache: true
    })

    useQuery({
        queryKey: buildQueryKey('filter-Contract Status'),
        queryFn: async () => await ApiClient.Filters.getContractStatuses(),
        cache: true
    })

    useQuery({
        queryKey: buildQueryKey('filter-Providers'),
        queryFn: async () => await ApiClient.Filters.getProvidersFilter(),
        cache: true
    })

    const onSubmit = (e: FormEvent) => {
        e.preventDefault()

        let selectedAction = options?.at(selected)

        if (text.startsWith(AI_SPOTLIGHT)) {
            selectedAction = {
                ...SpotlightManager.aiAction(),
                id: AI_SPOTLIGHT + '_' + Date.now().toString(),
                label: text.replace(AI_SPOTLIGHT, '')
            }
        }

        onAction(selectedAction)
    }

    const onAction = (action: ISpotlightItem) => {
        if (action) {
            const customParams = { ...action.params }
            switch (action.type) {
                case 'Provider':
                case 'Provider Groups':
                case 'Contract Status':
                case 'Contract Type':
                case 'Line Of Business':
                case 'Contracts':
                case 'Routes': {
                    dispatch(changeRoute({ route: action.params.route, params: action.params.params }))
                    break
                }
                default: break
            }
            onClose()
            dispatch(addSpotlightSearch({ id: action.id, params: customParams }))
        }
    }

    const onTyping = (e: ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value
        setText(val)

        if (val.length > 0) {
            const toSearch = val.toLowerCase()
            const hits = _.take(SpotlightManager.getActions().filter(x => x.keywords.some(k => x.exactMatchOnly
                ? k.toLowerCase() === toSearch
                : k.toLowerCase().includes(toSearch)
            )), 7)

            const filteredHits = hits.filter(hit =>
                hit.keywords.some(keyword => {
                    const mappedPermission = RoutePermissions[keyword.toLowerCase()]
                    return mappedPermission ? PermissionManager.hasPermission(mappedPermission, currentUser) : false
                })
            )

            setSearchResult(filteredHits)
            setSelected(v => _.max([v, hits.length]))
        } else {
            setSelected(-1)
            setSearchResult([])
        }
    }

    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'ArrowDown') {
            if (selected === -1) setSelected(0)
            else setSelected(v => (v + 1) % options?.length)
            e.preventDefault()
        } else if (e.key === 'ArrowUp') {
            if (selected === -1) setSelected(0)
            else setSelected(v => (v - 1 + options?.length) % options?.length)
            e.preventDefault()
        }
    }

    const onClose = () => {
        EventsEngine.publish('close-spotlight')
        setText('')
        setSearchResult([])
        setSelected(-1)
    }

    const renderSearchBody = () => {
        if (text.length > 0) {
            if (searchResult.length > 0) {
                const groups = _.groupBy(searchResult, x => x.type)
                return Object.keys(groups).map(key => {
                    const group = groups[key]
                    return (
                        <>
                            <ListSubheader sx={{ bgcolor: 'inherit', background: theme.palette.mode === 'dark' ? '#232323' : '#ffffff' }}>{key}</ListSubheader>
                            {group.map((x, i) => {
                                const indexSearchResult = searchResult.findIndex(k => k.id === x.id)

                                return (
                                    <ListItemButton key={i} selected={selected === indexSearchResult} onClick={() => onAction(x)}>
                                        {x?.icon && (
                                            <ListItemIcon>
                                                <RouteIcon icon={x?.icon} />
                                            </ListItemIcon>
                                        )}
                                        <ListItemText>
                                            <Typography component='span'>{x.label}</Typography>
                                        </ListItemText>
                                    </ListItemButton>
                                )
                            })}
                        </>
                    )
                })
            } else {
                return (
                    <Box justifyContent='center' alignItems='center' display='flex' flexDirection='column' height={100}>
                        <SearchOffRounded sx={{ mt: 1, mb: 1, color: 'gray', fontSize: '3rem' }} />
                        <Typography color='textDisabled' component='span'>
                            No results for &quot;
                            <strong>{text}</strong>
                            &quot;
                        </Typography>
                    </Box>
                )
            }
        } else {
            return (
                <>
                    <ListSubheader sx={{ bgcolor: 'inherit' }}>Recents</ListSubheader>
                    {SpotlightManager.historyToActions(history).filter(x => x).map((item, i) => {
                        return (
                            <ListItemButton key={i} selected={selected === i} onClick={() => onAction(item)}>
                                {item?.icon && (
                                    <ListItemIcon>
                                        <RouteIcon icon={item?.icon} />
                                    </ListItemIcon>
                                )}
                                <ListItemText>
                                    <Typography component='span'>{item?.label}</Typography>
                                </ListItemText>
                            </ListItemButton>
                        )
                    })}
                </>
            )
        }
    }

    return (
        <Card className={'spotlight-container' + (defaultClosed ? ' spotlight-closed' : '')}>
            <form onSubmit={onSubmit}>
                <TextField
                    fullWidth
                    autoFocus={!defaultClosed}
                    autoComplete='off'
                    placeholder='What are you looking for?'
                    variant='outlined'
                    value={text}
                    onChange={onTyping}
                    onKeyDown={onKeyDown}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='start'>
                                <SearchOutlined />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position='end'>
                                <Chip label='esc' size='small' variant='outlined' onClick={() => EventsEngine.publish('close-spotlight')} />
                            </InputAdornment>
                        )
                    }}
                />
            </form>
            <div className='spotlight-results'>
                <Divider />
                <CardContent>
                    <List sx={{ paddingTop: 0, paddingBottom: 0, overflowY: 'auto', maxHeight: '40vh' }}>
                        {renderSearchBody()}
                    </List>
                </CardContent>
            </div>
        </Card>
    )
}

export default Spotlight
